import React, { useState } from 'react';
import { 
  CreditCard, 
  QrCode, 
  Smartphone, 
  TrendingUp, 
  Users, 
  MapPin, 
  CheckCircle, 
  Zap,
  Globe,
  ArrowRight,
  Star,
  Shield,
  Clock,
  IndianRupee,
  Wifi,
  SmartphoneCharging,
  Banknote
} from 'lucide-react';

const WLALandingPage = () => {
  const [activeTab, setActiveTab] = useState('card');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center mb-6">
            <Globe className="w-12 h-12 text-cyan-400 mr-4" />
            <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
              Transforming Financial Access in India
            </h1>
          </div>
          
          <div className="text-lg md:text-xl text-gray-300 mb-12 space-y-4">
            <p>
              As banking evolves, so do customer expectations. <span className="text-cyan-400">White Label ATMs (WLA)</span> bridge 
              the digital and physical divide by providing <span className="text-purple-400">multi-mode withdrawal options</span>—empowering 
              users to transact either using their <span className="text-blue-400">ATM/debit card</span> or by simply 
              <span className="text-green-400"> scanning a QR code</span> on their smartphone.
            </p>
            <p>
              Whether in a metro city or a remote village, our technology ensures safe, fast, and reliable access to cash—without needing to visit a traditional bank branch.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://forms.gle/vTZ6m33eSZuwwvve9"
              className="bg-cyan-600 hover:bg-cyan-500 px-6 py-3 rounded-lg font-medium flex items-center justify-center"
            >
              Start Your ATM Point <ArrowRight className="ml-2" />
            </a>
            <button className="border border-purple-500 hover:bg-purple-500 px-6 py-3 rounded-lg font-medium flex items-center justify-center">
              Join as Partner <Users className="ml-2" />
            </button>
          </div>
        </div>
      </div>

      {/* Withdrawal Options Section */}
      <div className="py-16 bg-black/20">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 flex items-center justify-center">
            <Zap className="text-yellow-400 mr-3" /> Seamless Withdrawal – Your Way
          </h2>
          
          <p className="text-center text-gray-300 mb-8">
            Our system supports both Card-Based and Cardless QR-Based withdrawals to ensure complete flexibility:
          </p>

          <div className="flex flex-col md:flex-row gap-8">
            <div className="bg-gray-800/50 p-6 rounded-xl">
              <h3 className="text-2xl font-bold mb-4 flex items-center text-cyan-400">
                <CreditCard className="mr-2" /> Card-Based ATM Withdrawals:
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 mt-1 flex-shrink-0" />
                  Insert your debit or prepaid card
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 mt-1 flex-shrink-0" />
                  Enter your PIN securely
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 mt-1 flex-shrink-0" />
                  Select transaction type
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 mt-1 flex-shrink-0" />
                  Withdraw cash instantly
                </li>
              </ul>
            </div>

            <div className="bg-gray-800/50 p-6 rounded-xl">
              <h3 className="text-2xl font-bold mb-4 flex items-center text-purple-400">
                <QrCode className="mr-2" /> QR Code-Based Withdrawals:
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 mt-1 flex-shrink-0" />
                  Select "QR Withdrawal" on the ATM screen
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 mt-1 flex-shrink-0" />
                  A unique QR code appears
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 mt-1 flex-shrink-0" />
                  Open any UPI app (Paytm, PhonePe, Google Pay, BHIM, etc.)
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 mt-1 flex-shrink-0" />
                  Scan the QR and approve the payment
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-400 mr-2 mt-1 flex-shrink-0" />
                  Cash is dispensed – instantly, securely
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-6 flex items-center justify-center">
            <TrendingUp className="text-green-400 mr-3" /> Benefits for Local Operators & Franchise Partners
          </h2>
          
          <p className="text-center text-gray-300 mb-8">
            If you're looking to generate monthly income while promoting financial inclusion, becoming a White Label ATM franchise partner is the ideal business opportunity.
          </p>

          <div className="bg-gray-800/50 p-6 rounded-xl mb-8">
            <h3 className="text-2xl font-bold mb-4 text-yellow-400 flex items-center">
              <CheckCircle className="mr-2" /> Partner Benefits:
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <CheckCircle className="text-green-400 mr-2 mt-1 flex-shrink-0" />
                Affordable setup packages
              </li>
              <li className="flex items-start">
                <CheckCircle className="text-green-400 mr-2 mt-1 flex-shrink-0" />
                Commission on every cash withdrawal & transaction
              </li>
              <li className="flex items-start">
                <CheckCircle className="text-green-400 mr-2 mt-1 flex-shrink-0" />
                Minimal paperwork and quick installation
              </li>
              <li className="flex items-start">
                <CheckCircle className="text-green-400 mr-2 mt-1 flex-shrink-0" />
                Technical training and full support
              </li>
              <li className="flex items-start">
                <CheckCircle className="text-green-400 mr-2 mt-1 flex-shrink-0" />
                Eligible to offer additional services like:
                <ul className="ml-6 mt-2 space-y-2">
                  <li className="flex items-start">
                    <IndianRupee className="text-blue-400 mr-2 mt-1 flex-shrink-0" size={16} />
                    Aadhaar-enabled payments (AEPS)
                  </li>
                  <li className="flex items-start">
                    <SmartphoneCharging className="text-blue-400 mr-2 mt-1 flex-shrink-0" size={16} />
                    Mobile recharge, bill payments
                  </li>
                  <li className="flex items-start">
                    <Banknote className="text-blue-400 mr-2 mt-1 flex-shrink-0" size={16} />
                    Mini-statement, balance inquiry
                  </li>
                  <li className="flex items-start">
                    <Wifi className="text-blue-400 mr-2 mt-1 flex-shrink-0" size={16} />
                    Money transfer, micro-loans (optional)
                  </li>
                </ul>
              </li>
            </ul>
          </div>

          <p className="text-gray-300 text-center">
            With increasing demand in rural and semi-urban areas, WLA open doors for digital banking in previously underserved communities.
          </p>
        </div>
      </div>

      {/* Who Can Apply Section */}
      <div className="py-16 bg-black/20">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 flex items-center justify-center">
            <Users className="text-orange-400 mr-3" /> Who Can Apply for White Label ATM Installation?
          </h2>

          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              "CSPs (Customer Service Points)",
              "Shopkeepers & Retailers", 
              "Small businesses with foot traffic",
              "Entrepreneurs with space near markets, hospitals, stations, or panchayat offices",
              "Existing agents of banking or utility services"
            ].map((item, index) => (
              <div key={index} className="bg-gray-800/50 p-4 rounded-lg flex items-start">
                <CheckCircle className="text-green-400 mr-2 mt-1 flex-shrink-0" />
                {item}
              </div>
            ))}
          </div>

          <p className="text-gray-300 text-center">
            We provide complete installation, branding, and onboarding support for all our WLA partners.
          </p>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 flex items-center justify-center">
            <Globe className="text-cyan-400 mr-3" /> Join India's ATM Revolution Today
          </h2>
          
          <p className="text-gray-300 mb-8 text-lg">
            Our Card + QR Code-based White Label ATMs are the future of cash access in India. Whether you're a user looking for convenience or a business partner aiming to earn recurring income — this opportunity is your gateway to financial growth.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://forms.gle/vTZ6m33eSZuwwvve9"
              className="bg-cyan-600 hover:bg-cyan-500 px-6 py-3 rounded-lg font-medium flex items-center justify-center"
            >
              Apply Now <ArrowRight className="ml-2" />
            </a>
            <button className="bg-purple-600 hover:bg-purple-500 px-6 py-3 rounded-lg font-medium flex items-center justify-center">
              Start Your ATM Point Today <ArrowRight className="ml-2" />
            </button>
            <button className="border border-orange-500 hover:bg-orange-500 px-6 py-3 rounded-lg font-medium flex items-center justify-center">
              Join as Franchise Partner <Users className="ml-2" />
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 bg-black/40 border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4 text-center text-gray-400">
          <p>© {new Date().getFullYear()} White Label ATM Solutions. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default WLALandingPage;